import { useState, useEffect, Suspense } from 'react';
import { Posts } from './components/Posts';
import { ErrorBoundary } from './components/ErrorBoundary';

const STORAGE_KEY = 'bbs-username';

export default function App() {
  const [name, setName] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // 初回ロード時にlocalStorageから名前を読み込む
  useEffect(() => {
    const savedName = localStorage.getItem(STORAGE_KEY);
    if (savedName) {
      setName(savedName);
    }
  }, []);

  // 名前が変更されたらlocalStorageに保存
  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newName = e.target.value;
    setName(newName);
    if (newName.trim()) {
      localStorage.setItem(STORAGE_KEY, newName.trim());
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  };

  // 投稿を送信
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!message.trim()) {
      alert('本文を入力してください');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch('/api/posts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: name.trim() || '名無し',
          message: message.trim()
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit post');
      }

      // メッセージだけクリア（名前は保持）
      setMessage('');

      // SSEで自動的に更新されるため、手動リフレッシュは不要
    } catch (err) {
      alert('投稿に失敗しました: ' + (err instanceof Error ? err.message : '不明なエラー'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white border-2 border-black shadow-[4px_4px_0_rgba(0,0,0,0.2)]">
      <header className="bg-gradient-to-b from-blue-600 to-blue-700 text-white p-4 border-b-2 border-black text-center">
        <h1 className="text-2xl font-bold text-shadow-[2px_2px_0_rgba(0,0,0,0.3)] mb-1">YAPC::Fukuoka BBS</h1>
      </header>

      <section className="p-5 bg-gray-100 border-b-2 border-black">
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label htmlFor="name" className="block font-bold mb-1 text-sm">お名前:</label>
            <input
              type="text"
              id="name"
              value={name}
              onChange={handleNameChange}
              placeholder="名無しさん"
              maxLength={50}
              className="w-full p-2 border-2 border-gray-400 text-sm font-mono"
            />
          </div>
          <div className="mb-4">
            <label htmlFor="message" className="block font-bold mb-1 text-sm">本文:</label>
            <textarea
              id="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="メッセージを入力してください"
              maxLength={1000}
              className="w-full p-2 border-2 border-gray-400 text-sm font-mono resize-y min-h-20"
            />
          </div>
          <button
            type="submit"
            className="bg-gradient-to-b from-green-500 to-green-600 text-white px-8 py-2.5 border-2 border-black cursor-pointer text-base font-bold shadow-[2px_2px_0_rgba(0,0,0,0.3)] hover:from-green-600 hover:to-green-700 active:shadow-[1px_1px_0_rgba(0,0,0,0.3)] active:translate-x-[1px] active:translate-y-[1px] disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={submitting}
          >
            {submitting ? '送信中...' : '投稿する'}
          </button>
        </form>
      </section>

      <section className="p-5">
        <ErrorBoundary>
          <Suspense fallback={<div className="p-5 text-center">読み込み中...</div>}>
            <Posts />
          </Suspense>
        </ErrorBoundary>
      </section>

      <footer className="mt-5 text-center text-xs text-gray-600 pb-4">
        <p className="mb-2">Powered by Perl 5.42 + Hono + Bun + React + Turso + Cloudflare Workers + Cloudflare Containers</p>
        <p className="text-[10px] text-gray-500">
          <a href="https://github.com/kfly8/yapc-fukuoka-bbs" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
            GitHub
          </a>
          {' | '}
          <a href="https://yapcjapan.org/2025fukuoka/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
            YAPC::Fukuoka 2025
          </a>
        </p>
      </footer>
    </div>
  );
}
